import { gql } from 'graphql-tag';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useRevalidator } from 'react-router-dom';
import { useQuery } from 'urql';
import { Split } from '~/components/Split';
import { ContactList } from '~/components/contact/ContactList';
import { ContactListContext } from '~/components/contact/ContactListContext';
import { EmptyListActions } from '~/components/ui/EmptyListActions';
import { FilterGroupSearch } from '~/components/ui/FilterGroupSearch';
import { AddButton } from '~/components/ui/buttons/AddButton';
import type { ContactsQuery } from '~/generated/graphql';
import type { ContactTypeMappingsForListQuery } from '~/gql/graphql';
import { Status } from '~/gql/graphql';
import { useSearch } from '~/hooks/useSearch';
import { useSort } from '~/hooks/useSort';
import users from '~/images/images/users.png';
import { ContactsNav } from '~/layouts/nav/ContactsNav';
import { useContacts } from './resources/contacts';

type Contact = ReturnType<typeof useContacts>[number];

const SEARCH_OPTIONS = {
  keys: [
    'name',
    'type',
    'status',
    'site',
    {
      name: 'relationship',
      getFn(contact: Contact) {
        return contact.relationship?.join(', ') ?? '';
      },
    },
  ],
};

const CONTACT_TYPE_MAPPINGS_Q = gql`
  query ContactTypeMappingsForList {
    contactTypeMappings {
      label
      relationships
    }
  }
`;

const CONTACT_TYPES_PRESET_Q = gql`
  query ContactTypesPreset_ListFilters($preset: Boolean) {
    contactTypes(preset: $preset)
  }
`;

export default function ListContactsRoute() {
  const { contactId } = useParams();
  const { t, i18n } = useTranslation();
  const [view, setView] = useState('');
  const [filters, setFilters] = useState<URLSearchParams>(
    new URLSearchParams('status=Active')
  );
  const [query, setQuery] = useState('');
  const [navItems, setNavItems] = useState<string[]>([]);

  // Stable callbacks to prevent unnecessary re-renders in ContactsNav
  // These are passed as props and used in useEffect dependencies
  const handleViewChange = useCallback((newView: string) => {
    setView(newView);
  }, []);

  const handleNavItemsChange = useCallback((items: string[]) => {
    setNavItems(items);
  }, []);
  const contacts = useContacts({ includeInactive: true });
  const [{ data: mappingsData }] = useQuery<ContactTypeMappingsForListQuery>({
    query: CONTACT_TYPE_MAPPINGS_Q,
    requestPolicy: 'cache-and-network',
  });
  const [{ data: presetData }] = useQuery({
    query: CONTACT_TYPES_PRESET_Q,
    variables: { preset: true },
    requestPolicy: 'cache-and-network',
  });
  // Normalize mappings to a stable, non-nullable shape for downstream logic
  const mappings = useMemo(
    () =>
      (mappingsData?.contactTypeMappings ?? []).map((m) => ({
        label: (m?.label ?? '').trim(),
        relationships: (m?.relationships ?? []).map((r) => String(r)),
      })),
    [mappingsData]
  );
  const revalidator = useRevalidator();

  // Build label -> relationships index once per mappings change
  const labelToRelationshipsIndex = useMemo(() => {
    const index = new Map<string, Set<string>>();
    for (const m of mappings) {
      index.set(
        m.label.toLowerCase(),
        new Set(m.relationships.map((r) => String(r)))
      );
    }
    return index;
  }, [mappings]);

  // Compute set of relationships covered by nav items (excluding All/Other)
  const coveredRelationships = useMemo(() => {
    const base = navItems.filter((v) => v !== 'All' && v !== 'Other');
    const covered = new Set<string>();
    for (const lbl of base) {
      const set =
        labelToRelationshipsIndex.get(lbl.toLowerCase()) ?? new Set([lbl]);
      set.forEach((r) => covered.add(String(r)));
    }
    return covered;
  }, [navItems, labelToRelationshipsIndex]);

  // Filter contacts based on selected view (label semantics)
  const data = useMemo(() => {
    if (view === 'All' || !view) return contacts;
    if (view === 'Other') {
      return contacts.filter((contact) => {
        const rels = contact.relationship ?? [];
        return (
          rels.length === 0 ||
          rels.every((r) => !coveredRelationships.has(String(r)))
        );
      });
    }
    const set =
      labelToRelationshipsIndex.get(view.toLowerCase()) || new Set([view]);
    return contacts.filter((contact) => {
      const rels = contact.relationship ?? [];
      return rels.some((r) => set.has(String(r)));
    });
  }, [contacts, view, coveredRelationships, labelToRelationshipsIndex]);

  const VALID_STATUSES = useMemo(
    () => new Set(Object.values(Status) as string[]),
    []
  );
  const parseFilters = useCallback(
    (filters: URLSearchParams) => {
      const status = filters
        .getAll('status')
        .filter((s) => VALID_STATUSES.has(s)) as Status[];
      const relationship = filters.getAll('relationship').map((r) => String(r));
      return { status, relationship };
    },
    [VALID_STATUSES]
  );

  const { results, search } = useSearch(data, SEARCH_OPTIONS);
  const { sorted } = useSort(results, { default: 'name' });
  // Fallback to raw data if sorted results are not ready yet
  const listItems = sorted ?? data;
  // Normalize contacts to ensure a non-nullable relationship array for ContactList typing
  const normalizedListItems: Readonly<ContactsQuery['contacts']> = useMemo(
    () =>
      listItems.map((c) => ({
        ...c,
        relationship: (c.relationship ?? []).map((r) => String(r)),
      })),
    [listItems]
  );
  const refresh = () => revalidator.revalidate();

  const debounceHandle = useRef<number | null>(null);
  useEffect(() => {
    // Debounce to avoid excessive recalculation on rapid changes
    if (debounceHandle.current) window.clearTimeout(debounceHandle.current);
    debounceHandle.current = window.setTimeout(() => {
      search(query, parseFilters(filters));
    }, 120);
    return () => {
      if (debounceHandle.current) window.clearTimeout(debounceHandle.current);
    };
  }, [contacts, view, filters, query, navItems, search, parseFilters]);

  // Build relationship options once per mappings/contacts change
  const relationshipOptions = useMemo(() => {
    const opts: { label: string; value: string }[] = [];
    const seen = new Set<string>();
    // Backend presets from runtime; only these keys get translated
    const PRESET_REL = new Set<string>(
      (presetData?.contactTypes ?? []).map((v: unknown) => String(v))
    );

    for (const m of mappings) {
      for (const r of m.relationships) {
        const key = String(r);
        if (!seen.has(key)) {
          seen.add(key);
          const label = PRESET_REL.has(key)
            ? (() => {
                const k = `contactRelationships.${key}`;
                if (i18n.exists(k)) return t(k);
                const compact = key.replace(/\s+/g, '');
                const ck = `contactRelationships.${compact}`;
                return i18n.exists(ck) ? t(ck) : key;
              })()
            : key;
          opts.push({ label, value: key });
        }
      }
    }
    if (opts.length === 0) {
      const rels = new Set<string>();
      for (const c of contacts) {
        for (const r of c.relationship ?? []) rels.add(String(r));
      }
      return Array.from(rels)
        .sort((a, b) => a.localeCompare(b))
        .map((r) => ({ label: r, value: r }));
    }
    return opts;
  }, [mappings, contacts, t, i18n, presetData?.contactTypes]);

  return (
    <ContactListContext.Provider value={{ refresh }}>
      <AddButton label='Contact' />
      <Split
        count={results?.length}
        nav={
          <ContactsNav
            onViewChange={handleViewChange}
            initialView={view}
            onNavItemsChange={handleNavItemsChange}
          />
        }
        filters={
          <FilterGroupSearch
            filters={useMemo(
              () => [
                {
                  name: 'status' as const,
                  label: String(t('status')),
                  options: [
                    { label: 'Active', value: Status.Active },
                    { label: 'Inactive', value: Status.Inactive },
                  ],
                  searchable: false,
                  type: 'select' as const,
                },
                {
                  name: 'relationship' as const,
                  label: String(t('relationship')),
                  options: relationshipOptions,
                  type: 'select' as const,
                },
              ],
              [t, relationshipOptions]
            )}
            value={{ searchValue: query, filtersValue: filters }}
            onChange={({ searchValue, filtersValue }) => {
              setFilters(filtersValue);
              setQuery(searchValue);
            }}
          />
        }
        main={
          <>
            {listItems && listItems.length > 0 ? (
              <ContactList
                contacts={normalizedListItems}
                selectedId={contactId}
              />
            ) : data?.length === 0 &&
              view &&
              view !== 'All' &&
              view !== 'Other' ? (
              <EmptyListActions
                listType='Contact'
                image={users}
                imageAlt='Empty contacts illustration'
                typeLabel={
                  i18n.exists(`contactRelationships.${view}`)
                    ? t(`contactRelationships.${view}`, { count: 2 })
                    : view
                }
                buttonLabel={t('contact')}
              />
            ) : contacts?.length ? (
              <div className='max-w-full flex-1 overflow-x-hidden px-7 py-4'>
                There are no {t('contact_plural')} matching these filters...
              </div>
            ) : (
              <EmptyListActions listType='Contact' />
            )}
          </>
        }
      />
    </ContactListContext.Provider>
  );
}
